import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  RotateCcw, 
  Home, 
  RefreshCw, 
  Clock, 
  Hash, 
  Eye, 
  EyeOff, 
  Code, 
  Check,
  Upload,
  FileText,
  Lock
} from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useUser } from '../context/UserContext';
import { useFocusMode } from '../context/FocusModeContext';
import { TestConfig, ProgrammingLanguage } from '../types';
import { generateTestText, programmingLanguages } from '../utils/textGenerator';
import { customTextManager } from '../utils/customTextManager';
import { typingTestAPI, leaderboardAPI } from '../utils/apiClient';
import toast from 'react-hot-toast';

const TypingGame: React.FC = () => { // NOSONAR
  const location = useLocation();
  const navigate = useNavigate();
  const { startTest, resetTest, completeTest } = useGame();
  const { addXp, updateStats, state: { user } } = useUser();
  const isPremiumUser = user?.isPremium;

  const containerRef = useRef<HTMLDivElement>(null);
  
  // Test state
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [showResults, setShowResults] = useState(false);
  const { isFocusMode: focusMode, toggleFocusMode: toggleFocusModeContext } = useFocusMode();
  
  // Custom text and timer states (premium features)
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [customDurationValue] = useState(45);
  const [selectedCustomText, setSelectedCustomText] = useState<string | null>(null);
  const [showCustomTextModal, setShowCustomTextModal] = useState(false);
  const [customTexts, setCustomTexts] = useState<string[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  
  // State for punctuation and numbers toggles (initialized with false as default)
  const [includePunctuation, setIncludePunctuation] = useState(false);
  const [includeNumbers, setIncludeNumbers] = useState(false);
  
  // Mode selection state
  const [selectedMode, setSelectedMode] = useState<'time' | 'words' | 'code' | 'custom'>(
    (location.state?.mode as 'time' | 'words' | 'code' | 'custom') || 'time'
  );
  const [selectedDuration, setSelectedDuration] = useState(location.state?.duration || 30);
  const [selectedWordCount, setSelectedWordCount] = useState(location.state?.wordCount || 25);
  const [selectedLanguage, setSelectedLanguage] = useState<ProgrammingLanguage>(
    location.state?.language || programmingLanguages[0]
  );
  
  // Typing state
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [errors, setErrors] = useState<Set<number>>(new Set());
  const [testText, setTestText] = useState('');
  const [testWords, setTestWords] = useState<string[]>([]);
  
  // Performance tracking
  const [startTime, setStartTime] = useState<number | null>(null);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [correctChars, setCorrectChars] = useState(0);
  const [totalChars, setTotalChars] = useState(0);
  const textViewportRef = useRef<HTMLDivElement>(null);
  const [charsPerLine, setCharsPerLine] = useState(72);

  // Get test config from local state or navigation state
  
  const testConfig: TestConfig = useMemo(() => ({
    mode: selectedCustomText ? 'custom' : selectedMode,
    duration: isCustomDuration ? customDurationValue : selectedDuration,
    wordCount: selectedWordCount,
    language: selectedLanguage,
    punctuation: includePunctuation,
    numbers: includeNumbers,
    customText: selectedCustomText || undefined,
    isCustomTime: isCustomDuration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    selectedMode, 
    selectedDuration, 
    selectedWordCount, 
    selectedLanguage, 
    includePunctuation, 
    includeNumbers,
    selectedCustomText,
    isCustomDuration,
    customDurationValue
  ]);

  // Reset all test state
  const resetTestState = useCallback(() => {
    setCurrentCharIndex(0);
    setUserInput('');
    setErrors(new Set());
    setIsTestStarted(false);
    setShowResults(false);
    setStartTime(null);
    setWpm(0);
    setAccuracy(100);
    setCorrectChars(0);
    setTotalChars(0);
  }, []);

  // Initialize test text
  useEffect(() => {
    const { text, words } = generateTestText(testConfig);
    setTestText(text);
    setTestWords(words);
    setTimeLeft(selectedDuration);
    resetTestState();
    
    // Focus the container for keyboard events
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, [testConfig, selectedDuration, resetTestState]);

  // Load custom texts when user changes
  useEffect(() => {
    if (user?.id && isPremiumUser) {
      const texts = customTextManager.getCustomTexts(user.id);
      setCustomTexts(texts);
    }
  }, [user?.id, isPremiumUser]);

  // Reset custom settings when changing mode
  useEffect(() => {
    if (selectedMode !== 'time') {
      setIsCustomDuration(false);
    }
    
    if (selectedMode !== 'custom') {
      setSelectedCustomText(null);
    }
  }, [selectedMode]);

  // Calculate performance metrics
  const updatePerformanceMetrics = useCallback(() => {
    if (!startTime || totalChars === 0) return;

    // Calculate elapsed time in minutes
    let elapsedSeconds = (Date.now() - startTime) / 1000;
    
    // For time mode, use the time that has passed (duration - timeLeft) for more accurate WPM
    if (testConfig.mode === 'time' && isTestStarted) {
      elapsedSeconds = (testConfig.duration || 30) - timeLeft;
    }
    
    // Ensure minimum elapsed time to avoid division issues
    const elapsedMinutes = Math.max(elapsedSeconds / 60, 0.0166); // minimum 1 second
    
    // Standard WPM: (correct characters / 5) / time in minutes
    const wordsTyped = correctChars / 5; // Standard: 5 chars = 1 word
    const currentWpm = Math.round(wordsTyped / elapsedMinutes) || 0;
    const currentAccuracy = totalChars > 0 ? (correctChars / totalChars) * 100 : 100;

    setWpm(currentWpm);
    setAccuracy(currentAccuracy);
  }, [startTime, correctChars, totalChars, testConfig.mode, testConfig.duration, timeLeft, isTestStarted]);

  // Update metrics whenever typing data changes
  useEffect(() => {
    updatePerformanceMetrics();
  }, [updatePerformanceMetrics]);

  // Handle test completion
  const handleTestComplete = useCallback(() => {
    if (!startTime) return;

    // Calculate elapsed time - for time mode, use the set duration for accuracy
    let finalElapsedSeconds: number;
    if (testConfig.mode === 'time' || testConfig.mode === 'custom') {
      // Use the actual duration that was set
      finalElapsedSeconds = testConfig.duration || 30;
    } else {
      // For words/code mode, use actual elapsed time
      finalElapsedSeconds = (Date.now() - startTime) / 1000;
    }
    
    const finalElapsedMinutes = finalElapsedSeconds / 60;
    
    // Net WPM (only correct characters) - this is the main WPM metric
    const finalWpm = Math.round((correctChars / 5) / finalElapsedMinutes) || 0;
    const finalAccuracy = totalChars > 0 ? (correctChars / totalChars) * 100 : 100;

    // Update final metrics
    setWpm(finalWpm);
    setAccuracy(finalAccuracy);

    // Update game context
    completeTest();
    setShowResults(true);
    setIsTestStarted(false);
    
    // Award XP based on performance
    const xpGained = Math.floor(finalWpm * 2 + finalAccuracy);
    addXp(xpGained);

    // Save result to backend (fire-and-forget, don't block UI)
    if (user?.id) {
      const testPayload = {
        userId: user.id,
        wpm: finalWpm,
        accuracy: Math.round(finalAccuracy * 10) / 10,
        rawWpm: Math.round((totalChars / 5) / finalElapsedMinutes) || 0,
        mode: testConfig.mode,
        duration: finalElapsedSeconds,
        correctChars,
        incorrectChars: totalChars - correctChars,
        totalChars,
      };

      typingTestAPI.create(testPayload).catch(() => {});

      // Update leaderboard if this is a meaningful score
      if (finalWpm > 0) {
        leaderboardAPI.update({
          userId: user.id,
          username: user.username,
          country: user.country || 'US',
          wpm: finalWpm,
          accuracy: Math.round(finalAccuracy * 10) / 10,
        }).catch(() => {});
      }

      // Update local stats
      updateStats({
        totalTests: (user.stats?.totalTests || 0) + 1,
        avgWpm: Math.round(((user.stats?.avgWpm || 0) * (user.stats?.totalTests || 0) + finalWpm) / ((user.stats?.totalTests || 0) + 1)),
        bestWpm: Math.max(user.stats?.bestWpm || 0, finalWpm),
        avgAccuracy: Math.round(((user.stats?.avgAccuracy || 0) * (user.stats?.totalTests || 0) + finalAccuracy) / ((user.stats?.totalTests || 0) + 1) * 10) / 10,
        bestAccuracy: Math.max(user.stats?.bestAccuracy || 0, Math.round(finalAccuracy * 10) / 10),
      });
    }

    toast.success(`Test completed! +${xpGained} XP earned`);
  }, [startTime, correctChars, totalChars, testConfig, completeTest, addXp, user, updateStats]);

  // Keep a ref to always call the latest handleTestComplete (avoids stale closure in the timer interval)
  const handleTestCompleteRef = useRef(handleTestComplete);
  useEffect(() => {
    handleTestCompleteRef.current = handleTestComplete;
  }, [handleTestComplete]);
  
  // Toggle focus mode
  const toggleFocusMode = useCallback(() => {
    // Toggle focus mode state via context
    toggleFocusModeContext();
    
    // Keep the text input area focused
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }, 0);
  }, [toggleFocusModeContext]);

  // Handle custom text file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user?.id || !isPremiumUser) {
      toast.error('Custom text uploads are only available for premium users');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setFileUploadError(null);
      const success = await customTextManager.addCustomTextFromFile(user.id, file);
      
      if (success) {
        // Reload the list of custom texts
        const texts = customTextManager.getCustomTexts(user.id);
        setCustomTexts(texts);
        toast.success('Custom text uploaded successfully');
        
        // Select the newly uploaded text (it will be the last one in the array)
        if (texts.length > 0) {
          setSelectedCustomText(texts.at(-1)!);
        }
      } else {
        setFileUploadError('Failed to upload custom text');
        toast.error('Failed to upload custom text');
      }
    } catch (error: any) {
      setFileUploadError(error.message || 'Error uploading file');
      toast.error(error.message || 'Error uploading file');
    }
    
    // Reset the file input
    event.target.value = '';
  };
  
  // Handle selection of custom text
  const handleCustomTextSelect = (index: number) => {
    if (index >= 0 && index < customTexts.length) {
      setSelectedCustomText(customTexts[index]);
      toast.success('Custom text selected');
    }
  };
  
  // Handle removal of custom text
  const handleRemoveCustomText = (index: number) => {
    if (!user?.id) return;
    
    const success = customTextManager.removeCustomText(user.id, index);
    if (success) {
      // Reload the list of custom texts
      const texts = customTextManager.getCustomTexts(user.id);
      setCustomTexts(texts);
      
      // If the currently selected text was removed, clear the selection
      if (selectedCustomText === customTexts[index]) {
        setSelectedCustomText(null);
      }
      
      toast.success('Custom text removed');
    } else {
      toast.error('Failed to remove custom text');
    }
  };

  // Handle restart function
  const handleRestart = useCallback(() => {
    resetTest();
    resetTestState();
    setTimeLeft(selectedDuration);
    
    const { text, words } = generateTestText(testConfig);
    setTestText(text);
    setTestWords(words);
    
    // Focus the container after reset
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }, 100);
  }, [resetTest, resetTestState, testConfig, selectedDuration]);

  // Handle keyboard events for direct typing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { // NOSONAR
      if (showResults) return;
      
      // Toggle focus mode with Alt+F
      if (e.altKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        toggleFocusMode();
        return;
      }
      
      // Prevent default browser shortcuts but allow some
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          handleRestart();
        }
        
        return;
      }

      // Handle escape key
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/');
        return;
      }

      // Handle Tab key
      if (e.key === 'Tab') {
        e.preventDefault();
        return;
      }

      // Special handling for Enter key in code mode
      if (e.key === 'Enter' && testConfig.mode === 'code') {
        e.preventDefault();
        
        // Don't allow typing past the end of the text
        if (currentCharIndex >= testText.length) {
          return;
        }

        // Check if the next character is a newline character
        const expectedChar = testText[currentCharIndex];
        if (expectedChar === '\n') {
          // Start test if not started yet
          if (!isTestStarted) {
            setIsTestStarted(true);
            setStartTime(Date.now());
            startTest(testConfig, testText, testWords);
          }
          
          // Count this as a correct character
          setUserInput(prev => prev + '\n');
          setTotalChars(prev => prev + 1);
          setCorrectChars(prev => prev + 1);
          
          // Advance cursor position past the newline
          let nextCharIndex = currentCharIndex + 1;
          
          // Now check for indentation (spaces and tabs) and automatically skip them
          let indentation = '';
          while (
            nextCharIndex < testText.length && 
            (testText[nextCharIndex] === ' ' || testText[nextCharIndex] === '\t')
          ) {
            // Add each indentation character to userInput and count it as correct
            indentation += testText[nextCharIndex];
            nextCharIndex++;
          }
          
          if (indentation.length > 0) {
            setUserInput(prev => prev + indentation);
            setTotalChars(prev => prev + indentation.length);
            setCorrectChars(prev => prev + indentation.length);
          }
          
          // Set the cursor at the indented position
          setCurrentCharIndex(nextCharIndex);
          
          // Check if test is complete
          if (nextCharIndex >= testText.length) {
            setTimeout(handleTestComplete, 100);
          }
        }
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        // Only allow backspace if we're not at the beginning
        if (currentCharIndex > 0) {
          // Move back one character position
          const newCharIndex = currentCharIndex - 1;
          setCurrentCharIndex(newCharIndex);
          
          // Update user input
          const newInput = userInput.slice(0, -1);
          setUserInput(newInput);
          
          // Remove error for this position if it exists
          if (errors.has(newCharIndex)) {
            const newErrors = new Set(errors);
            newErrors.delete(newCharIndex);
            setErrors(newErrors);
          }
        }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        
        // Don't allow typing past the end of the text
        if (currentCharIndex >= testText.length) {
          return;
        }
        
        // Start test on first character
        if (!isTestStarted) {
          setIsTestStarted(true);
          setStartTime(Date.now());
          startTest(testConfig, testText, testWords);
        }
        
        const newChar = e.key;
        const expectedChar = testText[currentCharIndex];
        
        // Always update user input and increment character position
        setUserInput(prev => prev + newChar);
        setTotalChars(prev => prev + 1);
        
        // Check if the character matches and update metrics
        if (newChar === expectedChar) {
          setCorrectChars(prev => prev + 1);
        } else {
          // Add to errors set
          const newErrors = new Set(errors);
          newErrors.add(currentCharIndex);
          setErrors(newErrors);
        }
        
        // Always advance to next character position
        const nextCharIndex = currentCharIndex + 1;
        setCurrentCharIndex(nextCharIndex);
        
        // Check if test is complete
        if (testConfig.mode === 'words') {
          const wordsTyped = userInput.trim().split(/\s+/).length;
          if (wordsTyped >= (testConfig.wordCount || 25)) {
            setTimeout(handleTestComplete, 100);
          }
        } else if (nextCharIndex >= testText.length) {
          setTimeout(handleTestComplete, 100);
        }
      }
    };
    
    if (containerRef.current) {
      // Store a reference to the current DOM element
      const currentContainer = containerRef.current;
      currentContainer.addEventListener('keydown', handleKeyDown);
      return () => {
        // Use the stored reference in the cleanup function
        currentContainer.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [
    userInput,
    currentCharIndex,
    testText,
    isTestStarted,
    showResults,
    errors,
    testConfig,
    startTest,
    handleTestComplete,
    testWords,
    navigate,
    handleRestart,
    toggleFocusMode,
    focusMode
  ]);

  // Handle timer for time-based tests
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isTestStarted && (testConfig.mode === 'time' || testConfig.mode === 'custom') && !showResults) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTestCompleteRef.current();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
      return () => clearInterval(timer);
  }, [isTestStarted, testConfig.mode, showResults]);

  // Keep line length responsive to the actual viewport width to avoid text overflow.
  useEffect(() => {
    const viewport = textViewportRef.current;
    if (!viewport) return;

    const calculateCharsPerLine = () => {
      const style = globalThis.getComputedStyle(viewport);
      const fontSize = Number.parseFloat(style.fontSize) || 18;
      const letterSpacingRaw = Number.parseFloat(style.letterSpacing);
      const letterSpacing = Number.isFinite(letterSpacingRaw) ? letterSpacingRaw : 0;

      const perCharBase = testConfig.mode === 'code' ? fontSize * 0.69 : fontSize * 0.71;
      const perChar = Math.max(perCharBase + Math.max(letterSpacing, 0), 6);
      const usableWidth = Math.max(viewport.clientWidth - 32, 200);
      // Safety factor prevents right-edge clipping across browser font/rendering differences.
      const estimatedChars = Math.floor((usableWidth / perChar) * 0.9);

      const minChars = testConfig.mode === 'code' ? 30 : 35;
      const maxChars = testConfig.mode === 'code' ? 95 : 125;
      setCharsPerLine(Math.min(maxChars, Math.max(minChars, estimatedChars)));
    };

    calculateCharsPerLine();

    const resizeObserver = new ResizeObserver(calculateCharsPerLine);
    resizeObserver.observe(viewport);
    globalThis.addEventListener('resize', calculateCharsPerLine);

    return () => {
      resizeObserver.disconnect();
      globalThis.removeEventListener('resize', calculateCharsPerLine);
    };
  }, [testConfig.mode, focusMode]);

  // Create a proper MonkeyType-style text display
  const renderTextDisplay = () => { // NOSONAR
    if (!testText) return null;

    const isCodeMode = testConfig.mode === 'code';
    const displayHeight = isCodeMode
      ? 'clamp(12rem, 22vh, 16rem)'
      : 'clamp(9rem, 18vh, 12rem)';
    const lineHeight = isCodeMode
      ? 'clamp(2.4rem, 4.4vh, 3rem)'
      : 'clamp(2.6rem, 4.8vh, 3.3rem)';
    const fontSize = isCodeMode
      ? 'clamp(1.14rem, 1.58vw, 1.5rem)'
      : 'clamp(1.26rem, 1.9vw, 1.78rem)';

    // Calculate how many characters per line from measured viewport width
    const CHARS_PER_LINE = charsPerLine;
    const LINES_TO_SHOW = testConfig.mode === 'code' ? 5 : 3; // 5 lines for code, 3 for regular
    type RenderLine = { text: string; start: number };
    const lines: RenderLine[] = [];

    if (testConfig.mode === 'code') {
      const sourceLines = testText.split('\n');
      let sourceIndex = 0;

      sourceLines.forEach((sourceLine, sourceLineIndex) => {
        if (sourceLine.length === 0) {
          lines.push({ text: '', start: sourceIndex });
        } else {
          for (let offset = 0; offset < sourceLine.length; offset += CHARS_PER_LINE) {
            lines.push({ text: sourceLine.slice(offset, offset + CHARS_PER_LINE), start: sourceIndex + offset });
          }
        }

        sourceIndex += sourceLine.length;
        if (sourceLineIndex < sourceLines.length - 1) {
          sourceIndex += 1; // newline char in source text
        }
      });
    } else {
      let cursor = 0;
      while (cursor < testText.length) {
        if (testText.length - cursor <= CHARS_PER_LINE) {
          lines.push({ text: testText.slice(cursor), start: cursor });
          break;
        }

        const candidateEnd = Math.min(cursor + CHARS_PER_LINE, testText.length);
        let breakAt = testText.lastIndexOf(' ', candidateEnd);

        if (breakAt <= cursor) {
          breakAt = candidateEnd;
        }

        const lineText = testText.slice(cursor, breakAt);
        if (lineText.length > 0) {
          lines.push({ text: lineText, start: cursor });
        }

        cursor = breakAt;
        if (testText[cursor] === ' ') {
          cursor += 1;
        }
      }
    }

    if (lines.length === 0) {
      lines.push({ text: '', start: 0 });
    }

    let currentLineIndex = lines.length - 1;
    for (let i = 0; i < lines.length; i++) {
      const lineStart = lines[i].start;
      const lineEnd = lineStart + lines[i].text.length;
      if (currentCharIndex >= lineStart && currentCharIndex <= lineEnd) {
        currentLineIndex = i;
        break;
      }
    }

    const displayLines = lines.slice(currentLineIndex, currentLineIndex + LINES_TO_SHOW);
    
    return (
      <div className="flex flex-col justify-start items-center font-mono overflow-visible select-none w-full"
           style={{ 
             lineHeight,
             gap: '0rem',
             fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
             fontWeight: '400',
             height: displayHeight
           }}>
        {displayLines.slice(0, LINES_TO_SHOW).map((line, lineIndex) => {
          const lineStartChar = line.start;
          const nextLine = displayLines[lineIndex + 1];
          const hasActualNewlineAfter =
            Boolean(nextLine) && nextLine.start === line.start + line.text.length + 1;
          
          return (
              <div key={`line-${currentLineIndex + lineIndex}-${line.text.substring(0, 10)}`} 
                className={`flex items-center justify-center text-center w-full ${testConfig.mode === 'code' ? 'whitespace-pre' : ''}`}
                 style={{ 
                   height: lineHeight,
                   lineHeight,
                   marginBottom: '0',
                   display: 'flex',
                   alignItems: 'center',
                   fontSize,
                   fontFamily: testConfig.mode === 'code' ? '"Fira Code", "Monaco", "Consolas", monospace' : 'inherit',
                   overflow: 'visible'
                 }}>
              {line.text.split('').map((char, charIndex) => {
                const globalCharIndex = lineStartChar + charIndex;
                let className = 'relative ';
                
                if (globalCharIndex < currentCharIndex) {
                  // Characters already typed 
                  if (errors.has(globalCharIndex)) {
                    // Error characters
                    className += 'text-red-500 bg-red-500/20 ';
                  } else {
                    // Correctly typed characters
                    className += 'text-textMuted/80 ';
                  }
                } else if (globalCharIndex === currentCharIndex) {
                  // Current character to type - show clearly
                  className += 'text-text font-medium ';
                } else {
                  // Future characters - slightly dimmed
                  className += 'text-text/50 ';
                }
                
                return (
                  <span key={`char-${globalCharIndex}-${char}`} className={`${className} inline-block`} style={{ 
                    marginRight: testConfig.mode === 'code' ? '0.02rem' : '0.05rem',
                    fontSize,
                    height: lineHeight,
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: testConfig.mode === 'code' ? '"Fira Code", "Monaco", "Consolas", monospace' : 'inherit'
                  }}>
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                );
              })}
              {/* Add space at end of line if not last line */}
              {testConfig.mode === 'code' && hasActualNewlineAfter && (() => {
                const isCursorAtLineEnd = currentCharIndex === lineStartChar + line.text.length;
                const getSpaceColor = () => { // NOSONAR
                  if (testConfig.mode !== 'code') return 'transparent';
                  return isCursorAtLineEnd ? 'rgba(226, 183, 20, 0.8)' : 'rgba(100, 116, 139, 0.6)';
                };
                
                return (<span className="relative inline-block" style={{ 
                  marginRight: testConfig.mode === 'code' ? '0.02rem' : '0.05rem',
                  fontSize,
                  height: lineHeight,
                  display: 'inline-flex',
                  alignItems: 'center'
                }}>
                  <span className={testConfig.mode === 'code' ? 'px-1' : 'px-3'} style={{ 
                    width: testConfig.mode === 'code' ? '0.5rem' : '1rem',
                    color: getSpaceColor()
                  }}>
                    {testConfig.mode === 'code' ? '⏎' : ' '}
                  </span>
                </span>);
              })()}
            </div>
          );        
        })}
      </div>
    );
  }; 

  const StatsDisplay = () => { // NOSONAR
    // Calculate word count for words mode
    const currentWordCount = userInput.trim().split(/\s+/).filter(word => word.length > 0).length;
    const displayWordCount = Math.min(currentWordCount, testConfig.wordCount || 25);
    
    // Calculate accuracy class (helper function defined in parent scope)
    const accuracyClass = (() => {
      if (accuracy > 95) return 'text-green-500';
      if (accuracy > 85) return 'text-yellow-500';
      return 'text-red-500';
    })();
        
    return (
      <div className="flex justify-center space-x-10 mb-6">
        {/* WPM Stat */}
        <div className="flex flex-col items-center">
          <div className="text-xs text-textMuted uppercase tracking-wider mb-1">wpm</div>
          <div className="text-3xl font-bold text-accent">{wpm}</div>
        </div>
        
        {/* Accuracy Stat */}
        <div className="flex flex-col items-center">
          <div className="text-xs text-textMuted uppercase tracking-wider mb-1">acc</div>
          <div className={`text-3xl font-bold ${accuracyClass}`}>{accuracy.toFixed(1)}%</div>
        </div>
        
        {/* Time Mode: Show remaining time */}
        {testConfig.mode === 'time' && (
          <div className="flex flex-col items-center">
            <div className="text-xs text-textMuted uppercase tracking-wider mb-1">time</div>
            <div className="text-3xl font-bold text-accent">{timeLeft}s</div>
          </div>
        )}
        
        {/* Words Mode: Show word count */}
        {testConfig.mode === 'words' && (
          <div className="flex flex-col items-center">
            <div className="text-xs text-textMuted uppercase tracking-wider mb-1">words</div>
            <div className="text-3xl font-bold text-accent">
              {displayWordCount}/{testConfig.wordCount}
            </div>
          </div>
        )}
        
        {/* Errors Stat */}
        <div className="flex flex-col items-center">
          <div className="text-xs text-textMuted uppercase tracking-wider mb-1">err</div>
          <div className="text-3xl font-bold text-red-400">{errors.size}</div>
        </div>
      </div>
    );
  };
  
  const ResultsModal = () => { // NOSONAR
    if (!showResults) return null;
    
    const accuracyClass = (() => {
      if (accuracy > 95) return 'text-green-500';
      if (accuracy > 85) return 'text-yellow-500';
      return 'text-red-500';
    })();

    // Calculate time elapsed based on mode
    let timeElapsed: number;
    if (testConfig.mode === 'time' || testConfig.mode === 'custom') {
      // For time mode, use the set duration
      timeElapsed = testConfig.duration || 30;
    } else {
      // For other modes, use actual elapsed time
      timeElapsed = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
    }
    
    // Raw WPM includes all characters typed (correct + incorrect)
    const rawWpm = Math.round((totalChars / 5) / (timeElapsed / 60)) || 0;
    
    // Calculate characters per second
    const cps = totalChars / (timeElapsed || 1);

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
        <div className="bg-cardBg rounded-xl p-8 max-w-xl w-full mx-4 shadow-lg border border-accent/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-accent">Result</h2>
            <div className="flex space-x-2">
              <button
                onClick={handleRestart}
                className="bg-accent hover:bg-yellow-500 text-primary font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-secondary hover:bg-textMuted text-text font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </button>
            </div>
          </div>
          
          {/* MonkeyType-style Stats Display */}
          <div className="bg-secondary/30 rounded-xl p-6 mb-6">
            <div className="flex flex-col space-y-6">
              {/* Main Stats Row */}
              <div className="grid grid-cols-3 gap-8">
                <div className="flex flex-col">
                  <span className="text-textMuted text-xs uppercase tracking-wider">wpm</span>
                  <span className="text-4xl font-bold text-accent">{wpm}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-textMuted text-xs uppercase tracking-wider">accuracy</span>
                  <span className={`text-4xl font-bold ${accuracyClass}`}>{accuracy.toFixed(1)}%</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-textMuted text-xs uppercase tracking-wider">{testConfig.mode === 'time' ? 'duration' : 'time'}</span>
                  <span className="text-4xl font-bold text-text">{testConfig.mode === 'time' ? `${testConfig.duration}s` : `${timeElapsed}s`}</span>
                </div>
              </div>
              
              {/* Test Details - Time Mode */}
              {testConfig.mode === 'time' && (
                <div className="grid grid-cols-3 gap-6 pt-4 border-t border-border/30">
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">chars</span>
                    <span className="text-xl font-semibold text-text">{totalChars}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">chars/s</span>
                    <span className="text-xl font-semibold text-text">{cps.toFixed(1)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">raw</span>
                    <span className="text-xl font-semibold text-text">{rawWpm} wpm</span>
                  </div>
                </div>
              )}
              
              {/* Test Details - Words Mode */}
              {testConfig.mode === 'words' && (
                <div className="grid grid-cols-4 gap-6 pt-4 border-t border-border/30">
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">words</span>
                    <span className="text-xl font-semibold text-text">{testConfig.wordCount}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">chars</span>
                    <span className="text-xl font-semibold text-text">{totalChars}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">correct</span>
                    <span className="text-xl font-semibold text-green-500">{correctChars}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">incorrect</span>
                    <span className="text-xl font-semibold text-red-500">{totalChars - correctChars}</span>
                  </div>
                </div>
              )}
              
              {/* Test Details - Code Mode */}
              {testConfig.mode === 'code' && (
                <div className="grid grid-cols-4 gap-6 pt-4 border-t border-border/30">
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">language</span>
                    <div className="flex items-center">
                      <span 
                        className="w-3 h-3 inline-block rounded-full mr-1"
                        style={{ backgroundColor: testConfig.language?.color }}
                      ></span>
                      <span className="text-xl font-semibold text-text">{testConfig.language?.name || 'JavaScript'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">chars</span>
                    <span className="text-xl font-semibold text-text">{totalChars}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">time</span>
                    <span className="text-xl font-semibold text-text">{timeElapsed}s</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-textMuted text-xs">raw</span>
                    <span className="text-xl font-semibold text-text">{rawWpm} wpm</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* XP Gained */}
          <div className="bg-secondary/20 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-textMuted">XP Gained</span>
              <span className="text-lg font-bold text-yellow-500">+{Math.floor(wpm * 2 + accuracy)} XP</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`typing-game-container mx-auto w-full max-w-[960px] xl:max-w-[1100px] 2xl:max-w-[1240px] px-4 lg:px-6 ${focusMode ? 'focus-mode' : ''}`}>
      {/* Header Controls - Most are hidden in focus mode except the focus toggle */}
      <div className="flex flex-col space-y-4 mb-8">
        {/* Combined row with modes and action controls */}
        <div className="flex justify-between items-center min-h-[44px]">
          {/* Mode Selection Tabs */}
          <div className={`flex-grow flex justify-center ${focusMode || isTestStarted ? 'invisible' : ''}`}>
            <div className="flex space-x-2">
              {[
                { mode: 'time' as const, icon: Clock, label: 'Time' },
                { mode: 'words' as const, icon: Hash, label: 'Words' },
                { mode: 'code' as const, icon: Code, label: 'Code' }
              ].map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => {
                    setSelectedMode(mode);
                    // Reset custom settings when changing modes
                    if (mode !== 'time') {
                      setIsCustomDuration(false);
                      setSelectedCustomText(null);
                    }
                  }}
                  disabled={isTestStarted}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                    selectedMode === mode
                      ? 'bg-accent text-primary font-medium'
                      : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                  } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex space-x-2 ml-auto">
            <button
              onClick={toggleFocusMode}
              className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                focusMode 
                  ? 'bg-accent text-primary' 
                  : 'bg-secondary text-textMuted hover:text-accent'
              }`}
            >
              {focusMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>Focus</span>
            </button>
            <button
              onClick={handleRestart}
              className={`flex items-center space-x-1 px-3 py-2 bg-secondary hover:bg-accent/20 text-text rounded-lg transition-colors ${focusMode ? 'invisible' : ''}`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Restart</span>
            </button>
          </div>
        </div>

        {/* Second Row: Mode-specific Options and toggles in one row - hidden in focus mode */}
        <div className={`flex justify-center items-center min-h-[36px] ${focusMode || isTestStarted ? 'invisible' : ''}`}>
          {/* Time/Words Options */}
          <div className="flex justify-center mr-4">
            {selectedMode === 'time' && (
              <div className="flex space-x-1">
                {[15, 30, 60, 120].map((duration) => (
                  <button
                    key={duration}
                    onClick={() => {
                      setSelectedDuration(duration);
                      setIsCustomDuration(false);
                    }}
                    disabled={isTestStarted}
                    className={`px-3 py-1 rounded-lg transition-colors text-sm ${
                      selectedDuration === duration && !isCustomDuration
                        ? 'bg-accent text-primary font-medium'
                        : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                    } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                  >
                    {duration}s
                  </button>
                ))}
                
                {/* Custom Time button (Premium only) */}
                <button
                  onClick={() => {
                    if (isPremiumUser) {
                      setIsCustomDuration(true);
                    } else {
                      toast.error("Custom time is a premium feature");
                    }
                  }}
                  disabled={isTestStarted}
                  className={`px-3 py-1 rounded-lg transition-colors text-sm flex items-center ${
                    isCustomDuration
                      ? 'bg-accent text-primary font-medium'
                      : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                  } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                >
                  {!isPremiumUser && <Lock className="w-3 h-3 mr-1" />}
                  Custom
                </button>
                
                {/* Custom Text button (Premium only) */}
                <button
                  onClick={() => {
                    if (isPremiumUser) {
                      setShowCustomTextModal(true);
                    } else {
                      toast.error("Custom text is a premium feature");
                    }
                  }}
                  disabled={isTestStarted}
                  className={`px-3 py-1 rounded-lg transition-colors text-sm flex items-center ${
                    selectedCustomText
                      ? 'bg-accent text-primary font-medium'
                      : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                  } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                >
                  {!isPremiumUser && <Lock className="w-3 h-3 mr-1" />}
                  <FileText className="w-3 h-3 mr-1" />
                  Text
                </button>
              </div>
            )}

            {selectedMode === 'words' && (
              <div className="flex space-x-1">
                {[10, 25, 50, 100].map((wordCount) => (
                  <button
                    key={wordCount}
                    onClick={() => setSelectedWordCount(wordCount)}
                    disabled={isTestStarted}
                    className={`px-3 py-1 rounded-lg transition-colors text-sm ${
                      selectedWordCount === wordCount
                        ? 'bg-accent text-primary font-medium'
                        : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                    } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                  >
                    {wordCount}
                  </button>
                ))}
              </div>
            )}

            {selectedMode === 'code' && (
              <div className="flex space-x-1 overflow-x-auto max-w-[50vw] justify-center">
                {programmingLanguages.map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => setSelectedLanguage(lang)}
                    disabled={isTestStarted}
                    className={`px-2 py-1 rounded-lg transition-colors flex items-center text-sm ${
                      selectedLanguage.id === lang.id
                        ? 'bg-accent text-primary font-medium'
                        : 'bg-secondary text-textMuted hover:text-accent disabled:opacity-50'
                    } ${isTestStarted ? 'cursor-not-allowed' : ''}`}
                    style={{
                      borderLeft: `3px solid ${lang.color}`
                    } as React.CSSProperties}
                  >
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Punctuation and Numbers Toggles - side by side with options */}
          {(selectedMode === 'time' || selectedMode === 'words') && (
            <div className="flex space-x-3">
              <label className="flex items-center space-x-1 cursor-pointer">
                <div className={`w-4 h-4 rounded flex items-center justify-center ${
                  includePunctuation ? 'bg-accent' : 'bg-secondary'
                }`}>
                  {includePunctuation && <Check className="w-2 h-2 text-primary" />}
                </div>
                <input 
                  type="checkbox"
                  className="sr-only"
                  disabled={isTestStarted}
                  checked={includePunctuation}
                  onChange={() => !isTestStarted && setIncludePunctuation(!includePunctuation)}
                />
                <span className="text-xs text-textMuted">Punctuation</span>
              </label>
              
              <label className="flex items-center space-x-1 cursor-pointer">
                <div className={`w-4 h-4 rounded flex items-center justify-center ${
                  includeNumbers ? 'bg-accent' : 'bg-secondary'
                }`}>
                  {includeNumbers && <Check className="w-2 h-2 text-primary" />}
                </div>
                <input 
                  type="checkbox"
                  className="sr-only"
                  disabled={isTestStarted}
                  checked={includeNumbers}
                  onChange={() => !isTestStarted && setIncludeNumbers(!includeNumbers)}
                />
                <span className="text-xs text-textMuted">Numbers</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Stats - Hidden in focus mode */}
      <div className={`transition-opacity duration-300 min-h-[120px] ${
        focusMode ? 'opacity-0 invisible' : 'opacity-100 visible'
      }`}>
        <StatsDisplay />
      </div>
      
      {/* Main Typing Area */}      <div 
        ref={containerRef}
        tabIndex={0} /* NOSONAR */
        role="application"
        className={`mb-6 px-2 md:px-4 transition-all outline-none focus:outline-none focus-visible:outline-none ${isTestStarted ? '' : 'cursor-text'}`}
      >
        {/* MonkeyType-style Typing Text Display */}
        <div className="mb-6 flex items-center justify-center">
          <div className="w-full max-w-[104ch] xl:max-w-[118ch] mx-auto relative">
            {testText ? (
                  <div ref={textViewportRef} className="px-3 md:px-5 lg:px-7 xl:px-9 py-3 md:py-4 lg:py-5 transition-all overflow-visible" 
                   style={{ 
                     letterSpacing: '0.05em',
                     wordSpacing: testConfig.mode === 'code' ? '0em' : '0.09em',
                     height: (() => {
                       const isCodeMode = testConfig.mode === 'code';
                       if (focusMode) {
                         return isCodeMode ? 'clamp(14rem, 28vh, 18rem)' : 'clamp(11rem, 22vh, 14rem)';
                       }
                       return isCodeMode ? 'clamp(13rem, 25vh, 16rem)' : 'clamp(10rem, 20vh, 12rem)';
                     })(),
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     overflow: 'visible'
                   }}>
                {renderTextDisplay()}
              </div>
            ) : (
              <div className="text-center text-textMuted">
                <div className="text-xl mb-2">Loading text...</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Test Progress Indicator - Hidden in Focus Mode */}
        {isTestStarted && !focusMode && (
          <div className="mt-4 pt-4 border-t border-border/30">
            <div className="flex justify-between items-center text-sm text-textMuted">
              <span>
                {(() => {
                  if (testConfig.mode === 'time') return `${timeLeft}s remaining`;
                  if (testConfig.mode === 'words') {
                    const wordCount = Math.min(
                      userInput.trim().split(/\s+/).filter(w => w.length > 0).length,
                      testConfig.wordCount || 25
                    );
                    return `${wordCount} / ${testConfig.wordCount} words`;
                  }
                  return 'Keep typing...';
                })()}
              </span>
            </div>
            <div className="w-full bg-secondary/50 h-1 rounded-full mt-2">
              <div 
                className="bg-accent h-1 rounded-full transition-all duration-300"
                style={{ 
                  width: testConfig.mode === 'time' 
                    ? `${((testConfig.duration || 30) - timeLeft) / (testConfig.duration || 30) * 100}%`
                    : `${(currentCharIndex / testText.length) * 100}%`
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Test Configuration Info - Hidden in Focus Mode */}
      {!focusMode && (
        <div className="flex justify-between items-center text-textMuted text-sm">
          <div className="flex items-center space-x-4">
            {(() => {
              if (testConfig.mode === 'time') {
                return (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{testConfig.duration}s</span>
                  </div>
                );
              }
              if (testConfig.mode === 'words') {
                return (
                  <div className="flex items-center">
                    <Hash className="w-4 h-4 mr-1" />
                    <span>{testConfig.wordCount} words</span>
                  </div>
                );
              }
              return (
                <div className="flex items-center">
                  <span className="capitalize">{testConfig.mode} mode</span>
                </div>
              );
            })()}
          </div>
          
          <div className="flex space-x-4">
            {testConfig.punctuation && (
              <div className="text-accent">Punctuation</div>
            )}
            {testConfig.numbers && (
              <div className="text-accent">Numbers</div>
            )}
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help - Hidden in Focus Mode */}
      {!focusMode && (
        <div className="mt-12 text-center text-textMuted text-xs">
          <p>
            <kbd className="bg-secondary px-2 py-1 rounded text-text">Ctrl+R</kbd> to restart •{' '}
            <kbd className="bg-secondary px-2 py-1 rounded text-text ml-1">Alt+F</kbd> to toggle focus mode •{' '}
            <kbd className="bg-secondary px-2 py-1 rounded text-text ml-1">Esc</kbd> to quit
            {testConfig.mode === 'code' && (
              <span> •{' '}<kbd className="bg-secondary px-2 py-1 rounded text-text ml-1">Enter</kbd> for new line with auto-indent</span>
            )}
          </p>
        </div>
      )}
      
      {/* Advertisement Box - Only shown for non-premium users, remains visible in focus mode */}
      {!isPremiumUser && (
        <div className={`${focusMode ? 'mt-4' : 'mt-8'} p-4 bg-secondary/20 border border-secondary/30 rounded-lg`}>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-textMuted">Advertisement</h4>
            <button 
              onClick={() => navigate('/premium')}
              className="text-xs bg-accent/20 hover:bg-accent/40 text-accent px-2 py-1 rounded transition-colors"
            >
              Remove Ads
            </button>
          </div>
          <div className="bg-cardBg p-4 rounded min-h-[90px] flex items-center justify-center">
            <div className="text-center">
              <p className="text-textMuted mb-2">Upgrade to TypeRush Premium</p>
              <p className="text-xs text-textMuted/70">Enjoy an ad-free experience and unlock premium features</p>
            </div>
          </div>
        </div>
      )}

      <ResultsModal />
      
      {/* Custom Text Modal */}
      {showCustomTextModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-primary rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-text">Custom Texts</h3>
              <button 
                onClick={() => setShowCustomTextModal(false)}
                className="text-textMuted hover:text-accent"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-textMuted text-sm mb-2">
                Upload your own text files (.txt) to practice typing with your own content.
              </p>
              
              <div className="flex flex-col space-y-2">
                <label className="flex items-center justify-center border-2 border-dashed border-border p-4 rounded-lg cursor-pointer hover:border-accent transition-colors">
                  <Upload className="w-5 h-5 mr-2 text-textMuted" />
                  <span className="text-text">Select text file to upload</span>
                  <input
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                
                {fileUploadError && (
                  <p className="text-red-500 text-xs">{fileUploadError}</p>
                )}
                
                <p className="text-xs text-textMuted">
                  Max file size: 100KB. Files are stored locally in your browser.
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <h4 className="text-sm font-medium text-text mb-2">Your Custom Texts</h4>
              
              {customTexts.length === 0 ? (
                <p className="text-textMuted text-sm italic">No custom texts uploaded yet.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  {customTexts.map((text, index) => {
                    // Create stable key from text content
                    const textKey = `${text.slice(0, 20)}-${text.length}-${index}`;
                    return (
                    <div 
                      key={textKey}
                      className={`flex justify-between items-center p-2 rounded-md mb-1 ${
                        selectedCustomText === text
                          ? 'bg-accent/20 border-l-2 border-accent'
                          : 'hover:bg-secondary'
                      }`}
                    >
                      <button
                        onClick={() => handleCustomTextSelect(index)}
                        className="flex-1 text-left flex items-center"
                      >
                        <FileText className="w-4 h-4 mr-2 text-textMuted" />
                        <div className="flex-1">
                          <span className="text-sm text-text">
                            Custom Text {index + 1} 
                            <span className="text-xs text-textMuted ml-2">
                              ({text.length} chars, ~{Math.round(text.split(/\s+/).length / 5)} words)
                            </span>
                          </span>
                          <p className="text-xs text-textMuted truncate w-64">
                            {text.slice(0, 50)}...
                          </p>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => handleRemoveCustomText(index)}
                        className="text-textMuted hover:text-red-500 p-1"
                        aria-label="Remove custom text"
                      >
                        ✕
                      </button>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={() => {
                  setSelectedCustomText(null);
                  setShowCustomTextModal(false);
                }}
                className="px-4 py-2 rounded-md bg-secondary text-text hover:bg-secondary/70"
              >
                Clear Selection
              </button>
              
              <button
                onClick={() => setShowCustomTextModal(false)}
                className="px-4 py-2 rounded-md bg-accent text-primary hover:bg-accent/90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypingGame;
